import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL, MINISTRY, NAV_LINKS } from "@/lib/brand";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "shadow-[0_8px_30px_-12px_rgba(11,31,58,0.45)]" : ""
      }`}
      style={{ backgroundColor: "var(--navy-900)" }}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-3 group"
            aria-label="Abide Daily Ministry — Home"
          >
            <div className="w-12 h-12 lg:w-14 lg:h-14 flex items-center justify-center">
              <img src={LOGO_URL} alt="Abide Daily Ministry logo" className="w-full h-full object-contain" />
            </div>
            <div className="hidden sm:block leading-tight">
              <div className="font-display text-cream text-lg lg:text-xl tracking-wide">
                {MINISTRY.name.replace(" Ministry", "")}
              </div>
              <div className="text-[0.68rem] uppercase tracking-[0.22em] text-gold-light">
                Ministry
              </div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Toggle navigation"
            className="lg:hidden p-2 text-cream rounded-md hover:bg-white/5"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-white/10" style={{ backgroundColor: "var(--navy-900)" }} data-testid="mobile-menu">
          <nav className="px-5 py-4 flex flex-col gap-1" aria-label="Mobile">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                style={({ isActive }) => ({
                  color: isActive ? "var(--gold-400)" : "var(--cream-50)",
                  backgroundColor: isActive ? "rgba(255,255,255,0.06)" : "transparent",
                })}
                className="py-3 px-3 rounded-md text-base hover:bg-white/5 transition-colors"
                onMouseEnter={(e) => { if (!e.currentTarget.classList.contains("active")) e.currentTarget.style.color = "var(--gold-300)"; }}
                onMouseLeave={(e) => {
                  const isActive = e.currentTarget.getAttribute("aria-current") === "page";
                  e.currentTarget.style.color = isActive ? "var(--gold-400)" : "var(--cream-50)";
                }}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
